import {
  ApplicationRecord,
  OrderPaymentBreakdown,
  PaymentReconciliation,
} from '../types'

const mouzaNames = [
  'Dhanmondi',
  'Mirpur',
  'Gulshan',
  'Uttara',
  'Mohammadpur',
  'Tejgaon',
  'Banani',
  'Bashundhara',
]
const surveyNames = ['BS Survey', 'SA Survey', 'CS Survey', 'RS Survey']
const payerNames = [
  'Mohammad Karim',
  'Rahim Uddin',
  'Salma Khatun',
  'Jamal Hossain',
  'Nasrin Akter',
  'Rafiqul Islam',
  'Farjana Yasmin',
  'Abdul Malek',
  'Shahidul Islam',
  'Roksana Begum',
  'Aminul Haque',
  'Fatema Begum',
]
const phoneNumbers = [
  '01711234567',
  '01822345678',
  '01933456789',
  '01644567890',
  '01755678901',
  '01866789012',
  '01977890123',
  '01588901234',
  '01699012345',
  '01710123456',
]

const reconciliationMediums = [
  'bKash',
  'Nagad',
  'Rocket',
  'Dutch-Bangla Bank',
  'Sonali Bank',
  'Janata Bank',
  'DBBL',
  'City Bank',
]
const collectionMediums = ['EKPAY', 'Online Portal', 'Counter']

function randomDate(start: Date, end: Date): string {
  const d = new Date(
    start.getTime() + Math.random() * (end.getTime() - start.getTime()),
  )
  return d.toISOString().split('T')[0]
}

function randomNid(): string {
  return Math.floor(1000000000000 + Math.random() * 9000000000000).toString()
}

function randomOrderNumber(): string {
  return `1713508625${Math.floor(1000000 + Math.random() * 9000000)}`
}

function randomTransactionId(): string {
  const chars = '0123456789abcdef'
  let result = ''
  for (let i = 0; i < 17; i++)
    result += chars[Math.floor(Math.random() * chars.length)]
  return result
}

function randomChallanNumber(): string {
  return `CH-${Math.floor(100000 + Math.random() * 900000)}`
}

function randomEftNumber(): string {
  return `EFT-${Math.floor(100000 + Math.random() * 900000)}`
}

// Amount rules:
// Khatian + Online = 120
// Khatian + Office Counter = 120
// Khatian + Postal = 160
// Map + Office Counter = 545
// Map + Postal = 655
function getAmount(
  serviceType: 'Khatian' | 'Map',
  deliveryType: string,
): number {
  if (serviceType === 'Khatian') {
    return deliveryType === 'Postal Delivery (Local)' ? 160 : 120
  } else {
    return deliveryType === 'Postal Delivery (Local)' ? 655 : 545
  }
}

const deliveryTypes: Array<
  'Online' | 'Office Counter' | 'Postal Delivery (Local)'
> = ['Online', 'Office Counter', 'Postal Delivery (Local)']
const serviceTypes: Array<'Khatian' | 'Map'> = ['Khatian', 'Map']

export const applicationRecords: ApplicationRecord[] = Array.from(
  { length: 500 },
  (_, i) => {
    const serviceType =
      serviceTypes[Math.floor(Math.random() * serviceTypes.length)]
    // Map doesn't have Online delivery
    const availableDelivery =
      serviceType === 'Map'
        ? (['Office Counter', 'Postal Delivery (Local)'] as const)
        : deliveryTypes
    const deliveryType =
      availableDelivery[Math.floor(Math.random() * availableDelivery.length)]
    const amount = getAmount(serviceType, deliveryType)
    const mouza = mouzaNames[Math.floor(Math.random() * mouzaNames.length)]
    const survey = surveyNames[Math.floor(Math.random() * surveyNames.length)]
    const khatianNo = Math.floor(100 + Math.random() * 9900)

    return {
      id: `app-${i + 1}`,
      date: randomDate(new Date('2024-01-01'), new Date('2024-12-31')),
      payerName: payerNames[Math.floor(Math.random() * payerNames.length)],
      payerNid: randomNid(),
      amount,
      paymentMethod: 'EKPAY',
      orderNumber: randomOrderNumber(),
      transactionId: randomTransactionId(),
      serviceType,
      deliveryType,
      serviceInfo: `${mouza} - ${survey}, Khatian No: ${khatianNo}`,
    }
  },
).sort((a, b) => b.date.localeCompare(a.date))

export const orderPaymentBreakdowns: OrderPaymentBreakdown[] = Array.from(
  { length: 200 },
  (_, i) => {
    const serviceType =
      serviceTypes[Math.floor(Math.random() * serviceTypes.length)]
    const baseDate = randomDate(new Date('2024-01-01'), new Date('2024-12-31'))
    const courtFee = serviceType === 'Khatian' ? 20 : 50
    const postalFee = Math.random() > 0.5 ? 40 : 0
    const stationaryFee = serviceType === 'Khatian' ? 30 : 45
    const letterWriterFee = serviceType === 'Khatian' ? 30 : 50
    const totalAmount = courtFee + postalFee + stationaryFee + letterWriterFee

    return {
      id: `opb-${i + 1}`,
      date: baseDate,
      payerName: payerNames[Math.floor(Math.random() * payerNames.length)],
      payerNid: randomNid(),
      totalAmount,
      courtFee,
      courtFeeChallanNumber: randomChallanNumber(),
      courtFeeChallanDate: randomDate(
        new Date('2024-01-01'),
        new Date('2024-12-31'),
      ),
      postalFee,
      postalFeeChallanNumber: postalFee > 0 ? randomChallanNumber() : '-',
      postalFeeChallanDate:
        postalFee > 0
          ? randomDate(new Date('2024-01-01'), new Date('2024-12-31'))
          : '-',
      stationaryFee,
      stationaryFeeChallanNumber: randomChallanNumber(),
      stationaryFeeChallanDate: randomDate(
        new Date('2024-01-01'),
        new Date('2024-12-31'),
      ),
      letterWriterFee,
      letterWriterEftNumber: randomEftNumber(),
      letterWriterEftDate: randomDate(
        new Date('2024-01-01'),
        new Date('2024-12-31'),
      ),
      orderNumber: randomOrderNumber(),
      transactionId: randomTransactionId(),
      serviceType,
    }
  },
).sort((a, b) => b.date.localeCompare(a.date))

export const paymentReconciliations: PaymentReconciliation[] = Array.from(
  { length: 30 },
  (_, i) => {
    const serviceType =
      serviceTypes[Math.floor(Math.random() * serviceTypes.length)]
    const amountCollected =
      serviceType === 'Khatian'
        ? Math.random() > 0.3
          ? 120
          : 160
        : Math.random() > 0.3
          ? 545
          : 655
    const reconciliationMedium =
      reconciliationMediums[
        Math.floor(Math.random() * reconciliationMediums.length)
      ]
    const collectionDate = randomDate(
      new Date('2024-01-01'),
      new Date('2024-12-31'),
    )
    const reconciliationDate = randomDate(
      new Date('2024-01-01'),
      new Date('2024-12-31'),
    )

    return {
      id: `rec-${i + 1}`,
      date: collectionDate,
      payerName: payerNames[Math.floor(Math.random() * payerNames.length)],
      payerPhone: phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)],
      serviceName: serviceType,
      transactionId: randomTransactionId(),
      amountCollected,
      collectionMedium:
        collectionMediums[Math.floor(Math.random() * collectionMediums.length)],
      amountReconciled: amountCollected,
      reconciliationMedium,
      reconciliationDate,
      reference: `REF-${Math.floor(100000 + Math.random() * 900000)}`,
    }
  },
).sort((a, b) => b.date.localeCompare(a.date))
